import { Component, OnInit, ViewChild, Inject, HostListener, ElementRef } from "@angular/core";
import { Constant } from "../core/constant";
import { AdminService } from "../services/admin.service";
import { NgbModal, NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { FailModuleComponent } from "../fail-module/fail-module.component";
import { MatMenuTrigger } from "@angular/material/menu";
import { Router } from "@angular/router";
import { UsefulLinkList } from "../models/usefullink-list";
import { UsefulLink } from "../models/usefullink";
import { DeleteDialogComponent } from "../delete-dialog/delete-dialog.component";
import { CryptographyService } from "../services/cryptography.service";
import { WebStorageService, LOCAL_STORAGE } from "ngx-webstorage-service";
import { SnackbarService } from "../services/snackbar.service";

@Component({
  selector: "app-view-all-useful-links",
  templateUrl: "./view-all-useful-links.component.html",
  styleUrls: ["./view-all-useful-links.component.scss"],
})
export class ViewAllUsefulLinksComponent implements OnInit {
  activePopupId: string | number | null = null;
  loading: boolean = false;
  pageSizes: any[] = Constant.PageSizes;
  searchKey: string = "";

  page: number = 1;
  pageSize: number = 10;
  collectionSize: number = 0;
  totalPages: number = 0;

  usefulLinkList: UsefulLinkList = new UsefulLinkList();
  error: string = "";
  modalOption: NgbModalOptions = {};
  @ViewChild(MatMenuTrigger, { static: false }) trigger: MatMenuTrigger;
  @ViewChild("newLinkInput", { static: false }) newLinkInput: ElementRef;

  // Inline Edit variables
  editingLinkId: number | null = null;
  editLinkName: string = "";
  editLinkUrl: string = "";

  // Inline Add variables
  isAddingNewLink: boolean = false;
  newLinkName: string = "";
  newLinkUrl: string = "";

  constructor(
    private adminService: AdminService,
    private ngbModal: NgbModal,
    private router: Router,
    private cryptographyService: CryptographyService,
    private snackbarService: SnackbarService,
    @Inject(LOCAL_STORAGE) private storage: WebStorageService
  ) {}

  ngOnInit() {
    this.getUsefulLinks();
  }

  selectedItemsPerPageChange(event) {
    this.page = 1;
    this.pageSize = event.value;
    this.getUsefulLinks();
  }

  getUsefulLinks(): void {
    this.loading = true;
    this.adminService
      .GetUsefulLinkList(
        (this.page - 1) * this.pageSize,
        this.pageSize,
        this.searchKey
      )
      .subscribe(
        (data: UsefulLinkList) => {
          this.usefulLinkList.usefullinks = data.usefullinks.map(
            (o) => new UsefulLink(o)
          );
          this.collectionSize = data.count;
          this.totalPages = Math.ceil(this.collectionSize / this.pageSize);
          this.loading = false;
        },
        (error: string) => {
          this.error = error;
          const modalRef = this.ngbModal.open(FailModuleComponent);
          modalRef.componentInstance.errorMessage = error;
          this.loading = false;
        }
      );
  }

  addSearchKey(searchKey: string) {
    this.searchKey = searchKey;
    this.page = 1;
    this.getUsefulLinks();
  }

  togglePopup(event: Event, item: any) {
    event.stopPropagation();
    const id = item.id;
    this.activePopupId = this.activePopupId === id ? null : id;
  }

  @HostListener("document:click", ["$event"])
  onClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest(".grid-actions")) {
      this.activePopupId = null;
    }
  }

  goToPreviousPage() {
    if (this.page > 1) {
      this.page--;
      this.getUsefulLinks();
    }
  }

  goToNextPage() {
    if (this.page < this.totalPages) {
      this.page++;
      this.getUsefulLinks();
    }
  }

  onAddEditUsefulLink(usefulLinkId: number = null) {
    this.activePopupId = null;
    if (!usefulLinkId) {
      this.startAddNewLink();
    } else {
      const item = this.usefulLinkList.usefullinks.find((l) => l.id === usefulLinkId);
      if (item) {
        this.onEdit(item);
      }
    }
  }

  startAddNewLink() {
    this.cancelInlineEdit();
    this.isAddingNewLink = true;
    this.newLinkName = "";
    this.newLinkUrl = "";

    setTimeout(() => {
      if (this.newLinkInput && this.newLinkInput.nativeElement) {
        this.newLinkInput.nativeElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        this.newLinkInput.nativeElement.focus();
      }
    }, 100);
  }

  cancelAddNewLink() {
    this.isAddingNewLink = false;
    this.newLinkName = "";
    this.newLinkUrl = "";
  }

  saveNewLink() {
    if (!this.newLinkName || !this.newLinkUrl) {
      return;
    }
    this.loading = true;
    const newLink = new UsefulLink();
    newLink.id = 0;
    newLink.name = this.newLinkName;
    newLink.url = this.newLinkUrl;

    let decryptedId = 0;
    try {
      decryptedId = Number(
        this.cryptographyService.decryptData(this.storage.get("userId"))
      );
    } catch (e) {}

    if (decryptedId && decryptedId > 0) {
      newLink.createdUserId = decryptedId;
    }

    this.adminService.AddUsefulLink(newLink).subscribe(
      (res: any) => {
        this.loading = false;
        this.isAddingNewLink = false;
        this.newLinkName = "";
        this.newLinkUrl = "";
        this.snackbarService.showSuccess("Useful Link Added Successfully");
        this.getUsefulLinks();
      },
      (err: any) => {
        this.loading = false;
        const modalRef = this.ngbModal.open(FailModuleComponent);
        modalRef.componentInstance.errorMessage =
          typeof err === "string" ? err : "Failed to add useful link.";
      }
    );
  }

  onEdit(item: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.cancelAddNewLink();
    this.activePopupId = null;
    this.editingLinkId = item.id;
    this.editLinkName = item.name;
    this.editLinkUrl = item.url;
  }

  cancelInlineEdit() {
    this.editingLinkId = null;
    this.editLinkName = "";
    this.editLinkUrl = "";
  }

  saveInlineLink(item: any) {
    if (!this.editLinkName || !this.editLinkUrl) {
      return;
    }
    this.loading = true;
    const updatedLink = new UsefulLink(item);
    updatedLink.name = this.editLinkName;
    updatedLink.url = this.editLinkUrl;

    let decryptedId = 0;
    try {
      decryptedId = Number(
        this.cryptographyService.decryptData(this.storage.get("userId"))
      );
    } catch (e) {}

    if (decryptedId && decryptedId > 0) {
      updatedLink.modifiedUserId = decryptedId;
    } else {
      updatedLink.modifiedUserId = null;
    }

    this.adminService.UpdateUsefulLink(updatedLink).subscribe(
      (res: any) => {
        this.loading = false;
        this.editingLinkId = null;
        this.editLinkName = "";
        this.editLinkUrl = "";
        this.snackbarService.showSuccess("Useful Link Updated Successfully");
        this.getUsefulLinks();
      },
      (err: any) => {
        this.loading = false;
        const modalRef = this.ngbModal.open(FailModuleComponent);
        modalRef.componentInstance.errorMessage =
          typeof err === "string" ? err : "Failed to update useful link.";
      }
    );
  }

  closeMatMenuAndOpenDeleteModal(item, event) {
    if (this.trigger) {
      this.trigger.closeMenu();
    }
    setTimeout(() => {
      this.openDeleteModal(item, event);
    }, 100);
  }

  openDeleteModal(item, event) {
    if (event) {
      event.stopPropagation();
    }
    this.activePopupId = null;
    const modalRef = this.ngbModal.open(DeleteDialogComponent);
    modalRef.componentInstance.title =
      "Do you want to remove this link from the list? ";
    modalRef.componentInstance.deleteItem = item;
    modalRef.componentInstance.message =
      "This link will not appear on the home page after deletion.";

    if (this.trigger) {
      this.trigger.closeMenu();
    }

    modalRef.componentInstance.deletePopupEmit.subscribe((deleteItem) => {
      if (deleteItem != null) {
        let usefulLink = new UsefulLink(item);
        this.loading = true;
        
        let decryptedId = 0;
        try {
          decryptedId = Number(
            this.cryptographyService.decryptData(this.storage.get("userId"))
          );
        } catch (e) {}

        if (decryptedId && decryptedId > 0) {
          usefulLink.modifiedUserId = decryptedId;
        } else {
          usefulLink.modifiedUserId = null;
        }

        this.adminService
          .DeleteUsefulLink(usefulLink)
          .subscribe(
            (data: any) => {
              this.loading = false;
              this.snackbarService.showSuccess("Useful Link deleted successfully");
              this.page = 1;
              this.getUsefulLinks();
            },
            (err: any) => {
              this.loading = false;
              const failModal = this.ngbModal.open(FailModuleComponent);
              failModal.componentInstance.errorMessage = "Failed to delete useful link.";
            }
          );
      }
      this.ngbModal.dismissAll();
    });
  }
}
